<script setup>
/** 修改公会资料（/guilds/:slug/edit，负责人与运营）：只放资料表单；成员、金库、任务树、归档在公会管理页。 */
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Button, Input, Select, Textarea } from '@drama/ui'

import { api, getToken, loadAccount } from '../api.js'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const base = `/api/voko/guilds/${encodeURIComponent(route.params.slug)}`
const guild = ref(null)
const form = reactive({ name: '', description: '', handbook: '', github_repo: '', require_lead_approval: false, join_policy: 'approval' })
const error = ref('')
const joinPolicyOptions = computed(() => [{ value: 'approval', label: t('guilds.manageForm.policy.approval') }, { value: 'open', label: t('guilds.manageForm.policy.open') }])

onMounted(async () => {
  if (getToken() === null) {
    await router.push({ path: '/login', query: { next: route.fullPath } })
    return
  }
  try {
    const data = await api('GET', `${base}/manage`)
    guild.value = data.guild
    Object.assign(form, { name: data.guild.name, description: data.guild.description === null ? '' : data.guild.description, handbook: data.guild.handbook === null ? '' : data.guild.handbook, github_repo: data.guild.github_repo === null ? '' : data.guild.github_repo, require_lead_approval: data.guild.require_lead_approval, join_policy: data.guild.join_policy })
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
})

// 表单里的空白输入就是「不写」：简介、公告板和代码仓库存为 null。
async function save() {
  error.value = ''
  try {
    await api('PUT', base, { name: form.name, description: form.description.trim() === '' ? null : form.description, handbook: form.handbook.trim() === '' ? null : form.handbook, github_repo: form.github_repo.trim() === '' ? null : form.github_repo.trim(), require_lead_approval: form.require_lead_approval, join_policy: form.join_policy })
    await loadAccount()
    await router.push(`/guilds/${route.params.slug}/settings`)
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
}
</script>

<template>
  <header class="page-head">
    <div>
      <p class="page-head__eyebrow">{{ t('shell.manage') }}</p>
      <h1 class="page-head__title">{{ t('guilds.edit') }}{{ guild === null ? '' : ` · ${guild.name}` }}</h1>
    </div>
    <router-link :to="`/guilds/${route.params.slug}/settings`">{{ t('guilds.manageForm.back') }}</router-link>
  </header>
  <p v-if="error" role="alert">{{ error }}</p>
  <p v-if="guild !== null && guild.status === 'archived'">{{ t('guilds.manageForm.archivedNote') }}</p>
  <form v-else-if="guild !== null" class="voko-panel" @submit.prevent="save">
    <label class="voko-field">{{ t('guilds.create.name') }}<Input v-model="form.name" maxlength="120" required /></label>
    <label class="voko-field">{{ t('guilds.create.description') }}<Textarea v-model="form.description" :rows="2" /></label>
    <label class="voko-field">{{ t('guilds.handbook') }}<Textarea v-model="form.handbook" :rows="12" /><span class="voko-muted">{{ t('guilds.manageForm.handbookHint') }}</span></label>
    <label class="voko-field">{{ t('guilds.manageForm.githubRepo') }}<Input v-model="form.github_repo" maxlength="140" placeholder="owner/repo" /></label>
    <label class="voko-field">{{ t('guilds.manageForm.joinPolicy') }}<Select v-model="form.join_policy" :options="joinPolicyOptions" /></label>
    <label class="voko-check"><input v-model="form.require_lead_approval" type="checkbox"> {{ t('guilds.manageForm.requireApproval') }}</label>
    <Button type="submit" :disabled="form.name.trim() === ''">{{ t('guilds.manageForm.save') }}</Button>
  </form>
</template>
